import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useService } from '@/hooks/useService';
import { UserPreferenceService } from '@/services/UserPreferenceService';
import { UserPreferenceKey, UserPreferences, UserPreferenceValue, defaultUserPreferences, resolveUserPreferences } from '@/interfaces/UserPreference';

interface UserPreferencesContextType {
  /** Preferințele rezolvate. Până vin datele de la server conțin implicitele (nu blochează UI-ul). */
  preferences: UserPreferences;
  isLoading: boolean;
  /**
   * Salvează preferințele date și pune în stare răspunsul serverului.
   * `null` pe o cheie = revino la implicit. Aruncă eroarea mai departe, ca UI-ul să o poată afișa.
   */
  updatePreferences: (partial: { [K in UserPreferenceKey]?: UserPreferenceValue<K> | null }) => Promise<UserPreferences>;
}

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined);

/** Se montează în interiorul `UserProvider` — preferințele sunt ale utilizatorului autentificat. */
export const UserPreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const service = useService(UserPreferenceService);
  const [preferences, setPreferences] = useState<UserPreferences>(defaultUserPreferences);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let lastLoad = 0;
    const load = () => {
      lastLoad = Date.now();
      service
        .getMyPreferences()
        .then((entries) => {
          if (!cancelled) setPreferences(resolveUserPreferences(entries));
        })
        .catch((error) => {
          // Rămân valorile de până acum (la prima încărcare: implicitele — comportamentul de dinainte de preferințe).
          console.error('Preferințele utilizatorului nu s-au putut încărca; se folosesc valorile implicite.', error?.message);
        })
        .finally(() => {
          if (!cancelled) setIsLoading(false);
        });
    };
    load();
    // Salvarea din pagina de setări actualizează direct contextul; reîncărcarea asta prinde doar schimbările
    // făcute din alt tab sau alt dispozitiv, când utilizatorul revine aici (cel mult o dată la 30 s).
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastLoad > 30_000) load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [service]);

  const updatePreferences = useCallback<UserPreferencesContextType['updatePreferences']>(
    async (partial) => {
      const inputs = (Object.keys(partial) as UserPreferenceKey[]).map((key) => ({ key, value: partial[key] ?? null }));
      if (inputs.length === 0) return preferences;
      const entries = await service.updateMyPreferences(inputs);
      const resolved = resolveUserPreferences(entries);
      setPreferences(resolved);
      return resolved;
    },
    [service, preferences],
  );

  const value = useMemo(() => ({ preferences, isLoading, updatePreferences }), [preferences, isLoading, updatePreferences]);

  return <UserPreferencesContext.Provider value={value}>{children}</UserPreferencesContext.Provider>;
};

export const useUserPreferences = (): UserPreferencesContextType => {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error('useUserPreferences trebuie folosit în interiorul UserPreferencesProvider.');
  }
  return context;
};

/** Valoarea rezolvată a unei singure preferințe. */
export function useUserPreference<K extends UserPreferenceKey>(key: K): UserPreferenceValue<K> {
  return useUserPreferences().preferences[key];
}
